import {
  Document,
  Font,
  PDFViewer,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import WorkBold from "../../assets/fonts/WorkSans-Bold.ttf";
import WorkBoldItalic from "../../assets/fonts/WorkSans-BoldItalic.ttf";
import WorkRegular from "../../assets/fonts/WorkSans-Regular.ttf";
import WorkSemiBold from "../../assets/fonts/WorkSans-SemiBold.ttf";
import Loading from "../../components/Loading";
import { getLedgerByAll, getLedgerById } from "../../utils/api";

Font.register({
  family: "WorkSans",
  src: WorkRegular,
  fonts: [
    { src: WorkRegular },
    { src: WorkBold, fontWeight: "bold" },
    { src: WorkBoldItalic, fontWeight: "bold", fontStyle: "italic" },
    { src: WorkSemiBold, fontWeight: "semibold" },
  ],
});

const formatDateDMY = (dateValue) => {
  if (!dateValue) return "";
  const d = new Date(isNaN(dateValue) ? dateValue : Number(dateValue));
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    paddingTop: 20,
    paddingBottom: 25,
    paddingHorizontal: 25,
    fontSize: 9,
    fontFamily: "WorkSans",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 6,
  },
  companyName: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
  },
  companyAddress: {
    fontSize: 9,
    textAlign: "center",
    marginTop: 2,
  },
  gstin: {
    fontSize: 9,
    textAlign: "center",
    marginTop: 1,
  },
  ledgerTitle: {
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: 3,
    textAlign: "center",
    marginTop: 3,
  },
  dateRange: {
    fontSize: 9,
    textAlign: "center",
    marginTop: 2,
  },
  accountName: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
    marginTop: 3,
  },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    borderTopWidth: 1.5,
    borderTopColor: "#000000",
    borderBottomWidth: 1.5,
    borderBottomColor: "#000000",
    paddingVertical: 4,
    marginTop: 6,
    fontWeight: "bold",
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    paddingVertical: 2.5,
  },
  colDate: { width: 68, textAlign: "left" },
  colType: { width: 34, textAlign: "left" },
  colVch: { width: 75, textAlign: "left" },
  colParticulars: { width: 85, textAlign: "left" },
  colNarration: { flex: 1, textAlign: "left" },
  colDebit: { width: 68, textAlign: "right" },
  colCredit: { width: 68, textAlign: "right" },
  colBalance: { width: 75, textAlign: "right" },

  footerContainer: {
    marginTop: 4,
    borderTopWidth: 1.5,
    borderTopColor: "#000000",
    paddingTop: 3,
  },
  totalRow: {
    display: "flex",
    flexDirection: "row",
    paddingVertical: 2,
  },
  closingBalanceRow: {
    display: "flex",
    flexDirection: "row",
    paddingVertical: 2,
  },
  grandTotalWrapper: {
    marginTop: 2,
  },
  lineTop: {
    borderTopWidth: 1,
    borderTopColor: "#000000",
    marginBottom: 2,
  },
  grandTotalRow: {
    display: "flex",
    flexDirection: "row",
    paddingVertical: 1,
  },
  lineBottom1: {
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    marginTop: 2,
  },
  lineBottom2: {
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    marginTop: 1.5,
  },
});

const Ledger = () => {
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const params = useParams();

  const fetchLedgerData = async () => {
    try {
      setIsLoading(true);

      const [success, data] = await (params?.type === "all"
        ? getLedgerByAll(params?.id)
        : getLedgerById(params?.id));

      if (success) {
        setList(data);
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveFile = () => {
    const docName =
      (params.type === "all"
        ? list?.account?.name || list?.account?.userName || list?.customer?.name || list?.customer?.userName
        : list?.customer?.name || list?.customer?.userName || list?.account?.name || list?.account?.userName) || "account";
    pdf(<MyDocument list={list} />)
      .toBlob()
      .then((blob) =>
        saveAs(blob, `${docName}-ledger.pdf`)
      );
  };

  useEffect(() => {
    fetchLedgerData();
  }, [params.id, params?.type]);

  return isLoading ? (
    <Loading />
  ) : (
    <div>
      <button
        onClick={saveFile}
        className="bg-primary w-full py-2 text-center text-white font-medium text-sm h-10"
      >
        Save File
      </button>
      <PDFViewer
        className=""
        style={{ width: "100%", height: "calc(100vh - 40px)" }}
      >
        <MyDocument list={list} />
      </PDFViewer>
    </div>
  );
};

export default Ledger;

const MyDocument = ({ list }) => {
  const openingBalance = Number(list?.openingBalance) || 0;
  const startDateStr = formatDateDMY(list?.systemValues?.startDate);
  const endDateStr = formatDateDMY(list?.systemValues?.endDate);
  const accountName =
    list?.customer?.name ||
    list?.customer?.userName ||
    list?.account?.name ||
    list?.account?.userName ||
    "";

  let runningBal = openingBalance;
  let totalDebit = openingBalance > 0 ? openingBalance : 0;
  let totalCredit = openingBalance < 0 ? Math.abs(openingBalance) : 0;

  const rows = [];

  // 1. Opening Balance Row
  rows.push({
    isOpening: true,
    date: startDateStr,
    type: "",
    vchNo: "",
    particulars: "Opening Balance",
    narration: "",
    debit: openingBalance > 0 ? openingBalance : 0,
    credit: openingBalance < 0 ? Math.abs(openingBalance) : 0,
    balance: `${Math.abs(runningBal).toFixed(2)} ${runningBal >= 0 ? "Dr" : "Cr"}`,
  });

  // 2. Transaction Rows
  (list?.records || []).forEach((item) => {
    const isInvoice = item?.type === "Invoice";
    const isPayment = item?.type === "Payment";
    const isPurchase = item?.type === "PurchaseOrder";

    const typeStr = isInvoice ? "Sale" : isPayment ? "Rcpt" : isPurchase ? "Purc" : "";
    const vchStr = isInvoice ? (item?.invoiceNumberString || "") : "";
    const particularsStr = isInvoice
      ? "Invoice"
      : isPayment
      ? (item?.paymentType === "Received" ? "Received" : "Payment")
      : (item?.product?.productName || "Purchase");
    const narrationStr = item?.description || "";

    let debitVal = 0;
    let creditVal = 0;

    if (isInvoice) {
      debitVal = Number(item?.grandTotal) || 0;
    } else if (isPayment) {
      if (item?.paymentType === "Received") {
        creditVal = Number(item?.amount) || 0;
      } else {
        debitVal = Number(item?.amount) || 0;
      }
    } else if (isPurchase) {
      creditVal = Number(item?.totalAmount) || 0;
    }

    if (debitVal > 0) {
      runningBal += debitVal;
      totalDebit += debitVal;
    }
    if (creditVal > 0) {
      runningBal -= creditVal;
      totalCredit += creditVal;
    }

    rows.push({
      isOpening: false,
      date: formatDateDMY(item?.date ?? item?.invoiceDate),
      type: typeStr,
      vchNo: vchStr,
      particulars: particularsStr,
      narration: narrationStr,
      debit: debitVal,
      credit: creditVal,
      balance: `${Math.abs(runningBal).toFixed(2)} ${runningBal >= 0 ? "Dr" : "Cr"}`,
    });
  });

  const closingBalance = totalDebit - totalCredit;
  const isDebitBalance = closingBalance >= 0;
  const absClosingBalance = Math.abs(closingBalance);
  const grandTotal = Math.max(totalDebit, totalCredit);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          <Text style={styles.companyName}>PRABHU LABELS</Text>
          <Text style={styles.companyAddress}>
            D-298 Shaheed Bhagat Singh Nagar, Dhandra Road, Ludhiana
          </Text>
          <Text style={styles.gstin}>GSTIN : 03ABLPY4996M1ZM</Text>
          <Text style={styles.ledgerTitle}>L E D G E R</Text>
          <Text style={styles.dateRange}>
            (From {startDateStr} to {endDateStr} )
          </Text>
          <Text style={styles.accountName}>
            Account : {accountName}
          </Text>
        </View>

        {/* Table Header */}
        <View style={styles.tableHeader}>
          <Text style={styles.colDate}>Date</Text>
          <Text style={styles.colType}>Type</Text>
          <Text style={styles.colVch}>Vch No.</Text>
          <Text style={styles.colParticulars}>Particulars</Text>
          <Text style={styles.colNarration}>Narration</Text>
          <Text style={styles.colDebit}>Debit (`)</Text>
          <Text style={styles.colCredit}>Credit (`)</Text>
          <Text style={styles.colBalance}>Balance (`)</Text>
        </View>

        {/* Table Body */}
        {rows.map((row, index) => (
          <View style={styles.tableRow} key={index}>
            <Text style={styles.colDate}>{row.date}</Text>
            <Text style={styles.colType}>{row.type}</Text>
            <Text style={styles.colVch}>{row.vchNo}</Text>
            <Text style={styles.colParticulars}>{row.particulars}</Text>
            <Text style={styles.colNarration}>{row.narration}</Text>
            <Text style={styles.colDebit}>
              {row.debit > 0 ? Number(row.debit).toFixed(2) : ""}
            </Text>
            <Text style={styles.colCredit}>
              {row.credit > 0 ? Number(row.credit).toFixed(2) : ""}
            </Text>
            <Text style={styles.colBalance}>{row.balance}</Text>
          </View>
        ))}

        {/* Footer Totals */}
        <View style={styles.footerContainer}>
          {/* Row 1: Total */}
          <View style={styles.totalRow}>
            <View style={{ flex: 1, paddingRight: 10 }}>
              <Text style={{ textAlign: "right", fontWeight: "bold" }}>Total</Text>
            </View>
            <Text style={styles.colDebit}>
              {totalDebit > 0 ? Number(totalDebit).toFixed(2) : "0.00"}
            </Text>
            <Text style={styles.colCredit}>
              {totalCredit > 0 ? Number(totalCredit).toFixed(2) : "0.00"}
            </Text>
            <View style={styles.colBalance} />
          </View>

          {/* Row 2: Closing Balance */}
          <View style={styles.closingBalanceRow}>
            <View style={{ flex: 1, paddingRight: 10 }}>
              <Text style={{ textAlign: "right", fontWeight: "bold" }}>
                {isDebitBalance ? "Debit Balance" : "Credit Balance"}
              </Text>
            </View>
            <Text style={{ ...styles.colDebit, fontWeight: "bold" }}>
              {!isDebitBalance ? absClosingBalance.toFixed(2) : ""}
            </Text>
            <Text style={{ ...styles.colCredit, fontWeight: "bold" }}>
              {isDebitBalance ? absClosingBalance.toFixed(2) : ""}
            </Text>
            <View style={styles.colBalance} />
          </View>

          {/* Row 3: Grand Total */}
          <View style={styles.grandTotalWrapper}>
            <View style={styles.lineTop} />
            <View style={styles.grandTotalRow}>
              <View style={{ flex: 1, paddingRight: 10 }}>
                <Text style={{ textAlign: "right", fontWeight: "bold" }}>Grand Total</Text>
              </View>
              <Text style={{ ...styles.colDebit, fontWeight: "bold" }}>
                {Number(grandTotal).toFixed(2)}
              </Text>
              <Text style={{ ...styles.colCredit, fontWeight: "bold" }}>
                {Number(grandTotal).toFixed(2)}
              </Text>
              <View style={styles.colBalance} />
            </View>
            <View style={styles.lineBottom1} />
            <View style={styles.lineBottom2} />
          </View>
        </View>
      </Page>
    </Document>
  );
};
